import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { BookOpen, Film, Sparkles, HelpCircle, Youtube, Home as HomeIcon, MoonStar, ArrowLeft } from "lucide-react";
import { detectMedia } from "@/lib/media";
import { AuthedImage } from "@/components/AuthedMedia";
import StoriesPerWeekChoice from "@/components/StoriesPerWeekChoice";

// Shared with the landing page's Sunday "Weekly Recap" card — an
// illustrated teacher with a children's storybook library.
const WEEKLY_RECAP_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_ayah-daily-8/artifacts/zesqg5b4_image.png";

const DAY_ICONS = {
  1: <Film size={20} strokeWidth={2.5} />,
  2: <BookOpen size={20} strokeWidth={2.5} />,
  3: <Sparkles size={20} strokeWidth={2.5} />,
  4: <Film size={20} strokeWidth={2.5} />,
  5: <BookOpen size={20} strokeWidth={2.5} />,
  6: <Sparkles size={20} strokeWidth={2.5} />,
  7: <Youtube size={20} strokeWidth={2.5} />,
};

export default function Today() {
  const { user, refresh } = useAuth();
  const { t, getLoc } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveClass, setLiveClass] = useState(null);

  const load = async () => {
    try {
      const { data } = await client.get("/today");
      setData(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Per-week, not a single site-wide link — every cohort is on its own
    // pair of stories, so this returns whichever week's recap belongs to
    // this specific learner right now.
    client.get("/weekly-recap/current").then(({ data }) => setLiveClass(data)).catch(() => {});
  }, []);

  const toggleFamily = async () => {
    setBusy(true);
    try {
      await client.patch("/me/profile", { family_mode: !user.family_mode });
      await refresh();
      await load();
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-12 text-hv-slate" data-testid="today-loading">Loading your journey…</div>;

  if (!data.enrolled) {
    return (
      <main className="max-w-2xl mx-auto px-5 py-14" data-testid="today-not-enrolled">
        <h1 className="hv-heading text-3xl text-hv-navy">{t("not_enrolled")}</h1>
      </main>
    );
  }

  const pos = data.position;
  const stats = data.stats;
  const day = pos.day_in_cycle;
  const currentStory = data.current_story;

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="today-page">
      <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-bold text-hv-navy text-base shadow-sm hover:bg-hv-cream transition mb-5">
        <ArrowLeft size={18} strokeWidth={2.5} /> Back
      </Link>
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="hv-badge"><HomeIcon size={12} /> {t("today")}</span>
        <span className="hv-badge hv-badge-coral" data-testid="cycle-indicator">
          {t("days_left_in_cycle").replace("{n}", day)}
        </span>
        <button data-testid="family-mode-toggle" onClick={toggleFamily} disabled={busy}
                className={`hv-badge ${user.family_mode ? "hv-badge-coral" : ""} cursor-pointer`}>
          <MoonStar size={12} /> {t("family_mode")} {user.family_mode ? "· ON" : "· OFF"}
        </button>
      </div>

      <div className="mt-5">
        <StoriesPerWeekChoice />
      </div>

      <h1 className="hv-heading mt-5 text-3xl sm:text-4xl">
        <span className="text-hv-navy">Cycle {pos.cycle_number} —</span>{" "}
        <span className="text-hv-coral">Day {day} of 7</span>
      </h1>

      {/* 7-dot day tracker */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="day-tracker">
        {[1,2,3,4,5,6,7].map((d) => {
          const state = d < day ? "done" : d === day ? "active" : "upcoming";
          return (
            <div key={d} className={`hv-dot hv-dot-${state}`} data-testid={`day-dot-${d}`}>
              {DAY_ICONS[d]}
            </div>
          );
        })}
      </div>

      {/* Stat row */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <div className="hv-card p-5" data-testid="stat-juz">
          <div className="text-4xl font-heading font-bold text-hv-teal">{stats.juz_completed}</div>
          <div className="text-xs uppercase tracking-wider text-hv-slate mt-1 font-bold">{t("juz_done")}</div>
        </div>
        <div className="hv-card p-5" data-testid="stat-stories">
          <div className="text-4xl font-heading font-bold text-hv-teal">{stats.stories_completed}</div>
          <div className="text-xs uppercase tracking-wider text-hv-slate mt-1 font-bold">{t("stories_done")}</div>
        </div>
        <div className="hv-card p-5" data-testid="stat-streak">
          <div className="text-4xl font-heading font-bold text-hv-teal">{stats.day_streak || 0}</div>
          <div className="text-xs uppercase tracking-wider text-hv-slate mt-1 font-bold">{t("streak")}</div>
        </div>
      </div>

      {/* Current story CTA */}
      {currentStory ? (
        <div className="mt-8 hv-card p-6 sm:p-8 grid lg:grid-cols-12 gap-6 items-center" data-testid="current-story-card">
          <div className="lg:col-span-4">
            <div className="rounded-2xl overflow-hidden aspect-square bg-hv-navy">
              <AuthedImage src={currentStory.storybook?.[0]?.image_url || "https://images.unsplash.com/photo-1705668403533-3c17914a497a"}
                   className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <span className="hv-badge">Juz {currentStory.juz_number} · Story {currentStory.story_number_in_juz}</span>
            <h2 className="hv-heading mt-3 text-2xl sm:text-3xl text-hv-navy">{getLoc(currentStory.title)}</h2>
            <p className="mt-3 text-hv-slate">
              {pos.current_content_type === "video" && "Watch today's 5-minute animated story."}
              {pos.current_content_type === "storybook" && "Read the illustrated storybook version."}
              {pos.current_content_type === "learn" && "Learn the morals, ayah, word of the day, and take the quiz."}
              {pos.current_content_type === "write" && "Write two short parts: the Quran story and the life lesson, up to 500 words each."}
              {pos.current_content_type === "live" && t("live_soon")}
            </p>
            <div className="mt-5 flex gap-3">
              <Link to={`/story/${currentStory.global_sequence}?tab=${pos.current_content_type}`}
                    data-testid="open-current-story" className="hv-btn-primary">
                {pos.current_content_type === "video" && t("watch_video")}
                {pos.current_content_type === "storybook" && t("read_story")}
                {pos.current_content_type === "learn" && t("take_quiz")}
                {pos.current_content_type === "write" && "Write your reflection"}
                {pos.current_content_type === "live" && "Open Live"}
              </Link>
              <Link to="/dashboard" data-testid="open-progress" className="hv-btn-secondary">{t("progress")}</Link>
            </div>
          </div>
        </div>
      ) : (
        (() => {
          const media = liveClass?.enabled ? detectMedia(liveClass.youtube_url) : { type: "none" };
          const hasEmbed = media.type !== "none" && media.type !== "direct" && !!media.src;
          return (
            <div className="mt-8 hv-card p-6 sm:p-8 grid lg:grid-cols-12 gap-6 items-center" data-testid="live-day-card">
              <div className="lg:col-span-4">
                {hasEmbed ? (
                  <div className="rounded-2xl overflow-hidden aspect-square bg-hv-navy">
                    <iframe
                      data-testid="weekly-recap-iframe"
                      className="w-full h-full"
                      src={media.src}
                      title="This week's recap video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="rounded-2xl overflow-hidden aspect-square bg-hv-navy relative">
                    <img src={WEEKLY_RECAP_IMG} alt="" className="w-full h-full object-cover" />
                    <span className="absolute top-3 start-3 hv-badge hv-badge-coral">
                      <Youtube size={13} strokeWidth={3} /> YouTube
                    </span>
                  </div>
                )}
              </div>
              <div className="lg:col-span-8">
                <span className="hv-badge">Sunday · Weekly Recap</span>
                <h2 className="hv-heading mt-3 text-2xl sm:text-3xl text-hv-navy">This week's recap video</h2>
                <p className="mt-3 text-hv-slate">
                  Your child's teacher records a short recap of this week's story and its quiz,
                  and the word of the day — posted here and on YouTube.
                </p>
                {hasEmbed ? (
                  liveClass?.schedule_note && (
                    <div className="mt-5 hv-badge" data-testid="recap-schedule-note">{liveClass.schedule_note}</div>
                  )
                ) : (
                  <div className="mt-5 hv-badge" data-testid="recap-coming-soon">{t("live_soon")}</div>
                )}
              </div>
            </div>
          );
        })()
      )}

      {/* Week's two stories */}
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {[data.story_a, data.story_b].filter(Boolean).map((s, i) => (
          <Link to={`/story/${s.global_sequence}`} key={s.global_sequence}
                data-testid={`week-story-${i}`}
                className="hv-card p-5 flex gap-4 items-center hover:shadow-lg transition">
            <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 bg-hv-navy">
              {/* Cover art is public even for locked stories; storybook pages need the story unlocked, so prefer the cover. */}
              <AuthedImage src={s.cover_image_gridfs_id ? `/api/stories/${s.global_sequence}/media/cover-image` : (s.storybook?.[0]?.image_url || "https://images.unsplash.com/photo-1705668403533-3c17914a497a")} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-hv-teal">Story {i === 0 ? "A" : "B"} · Juz {s.juz_number}</div>
              <div className="hv-heading text-hv-navy text-lg mt-1">{getLoc(s.title)}</div>
            </div>
          </Link>
        ))}
      </div>

      {err && <div className="mt-6 text-hv-coral font-bold">{err}</div>}
    </main>
  );
}
